import React, { useEffect, useState, useCallback } from "react";
import {
  fetchTopics,
  fetchTopicDetail,
  patchSense,
  createSense,
  patchScenarioTurn,
  deleteScenarioTurn,
  createScenarioTurn,
  createQuizWord,
  fetchBuiltinQuranicTopics,
  fetchBuiltinTopicWordOverrides,
  saveBuiltinTopicWordOverride,
  deleteBuiltinTopicWordOverride,
} from "../api";

/**
 * AdminQuizEditor
 *
 * Top-level mode: "Arabic Topics" | "Quranic Curriculum"
 *
 * Arabic Topics sub-tabs:
 *  - "Words"     → view/fix word definitions (word_meaning questions)
 *  - "Sentences" → view/edit sentence-building questions (ScenarioTurns)
 *
 * Quranic Curriculum:
 *  - Select a built-in topic, then edit/override the correct meanings for quiz words.
 */
export default function AdminQuizEditor() {
  const [editorMode, setEditorMode] = useState("arabic"); // "arabic" | "quran"
  const [topics, setTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [topicDetail, setTopicDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("words"); // "words" | "sentences"
  const [wordFilter, setWordFilter] = useState("missing"); // "missing" | "all"

  // Load topics on mount
  useEffect(() => {
    fetchTopics()
      .then((data) => {
        const sorted = [...data].sort((a, b) => {
          const ao = a.order ?? 999999;
          const bo = b.order ?? 999999;
          if (ao !== bo) return ao - bo;
          return (a.name || "").localeCompare(b.name || "");
        });
        setTopics(sorted);
        if (sorted.length > 0 && !selectedTopicId) {
          setSelectedTopicId(String(sorted[0].id));
        }
      })
      .catch(() => setError("Failed to load topics"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDetail = useCallback(() => {
    if (!selectedTopicId) return;
    setLoading(true);
    setError(null);
    setTopicDetail(null);
    fetchTopicDetail(selectedTopicId)
      .then((d) => setTopicDetail(d))
      .catch(() => setError("Failed to load topic details"))
      .finally(() => setLoading(false));
  }, [selectedTopicId]);

  useEffect(() => { loadDetail(); }, [loadDetail]);

  const words = topicDetail?.words || [];
  const turns = topicDetail?.scenario_turns || [];

  const hasMissingDef = (w) => {
    const senses = w.senses || [];
    if (senses.length === 0) return true;
    return !senses.some((s) => s.definition && s.definition.trim() && !containsArabic(s.definition));
  };

  const displayedWords = wordFilter === "missing" ? words.filter(hasMissingDef) : words;
  const missingCount = words.filter(hasMissingDef).length;

  const TABS = [
    { id: "words", label: `Words${missingCount > 0 ? ` (${missingCount} ⚠)` : ""}` },
    { id: "sentences", label: `Sentences (${turns.length})` },
  ];

  return (
    <div className="space-y-4">
      {/* Top-level mode selector */}
      <div className="flex gap-1 border-b border-slate-200 pb-0">
        {[
          { id: "arabic", label: "Arabic Topics" },
          { id: "quran", label: "Quranic Curriculum" },
        ].map((m) => (
          <button
            key={m.id}
            onClick={() => setEditorMode(m.id)}
            className={`px-4 py-2 text-sm font-semibold rounded-t-lg border-b-2 transition-colors -mb-px ${
              editorMode === m.id
                ? "border-emerald-600 text-emerald-700 bg-emerald-50"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {editorMode === "quran" && <QuranicCurriculumQuizEditor />}

      {editorMode === "arabic" && (<div className="space-y-4">
      {/* Topic selector */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
          Topic
        </label>
        <select
          value={selectedTopicId}
          onChange={(e) => setSelectedTopicId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
        >
          {topics.map((t) => (
            <option key={t.id} value={String(t.id)}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Inner tabs: Words / Sentences */}
      <div className="flex gap-1 border-b border-slate-100 pb-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-t-lg border-b-2 transition-colors -mb-px ${
              activeTab === tab.id
                ? "border-violet-600 text-violet-700 bg-violet-50"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* ── Words tab ─────────────────────────────────────── */}
      {!loading && topicDetail && activeTab === "words" && (
        <div className="space-y-3">
          {/* Filter */}
          <div className="flex gap-2">
            {[
              { key: "missing", label: "Missing definitions" },
              { key: "all", label: "All words" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setWordFilter(key)}
                className={`px-3 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                  wordFilter === key
                    ? "bg-violet-600 text-white border-violet-600"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Word list */}
          {displayedWords.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              {wordFilter === "missing" ? (
                <><p className="text-2xl mb-1">✅</p><p className="text-sm">All words have definitions!</p></>
              ) : (
                <p className="text-sm">No words in this topic.</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-400 px-1">
                {wordFilter === "missing"
                  ? `${displayedWords.length} word${displayedWords.length !== 1 ? "s" : ""} need a definition`
                  : `${displayedWords.length} word${displayedWords.length !== 1 ? "s" : ""} total`
                }
              </p>
              {displayedWords.map((word) => (
                <WordDefEditor key={word.id} word={word} isMissing={hasMissingDef(word)} onSaved={loadDetail} />
              ))}
            </div>
          )}

          {/* Add new word question */}
          <AddWordForm topicId={selectedTopicId} onSaved={loadDetail} />
        </div>
      )}

      {/* ── Sentences tab ─────────────────────────────────── */}
      {!loading && topicDetail && activeTab === "sentences" && (
        <div className="space-y-3">
          {turns.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <p className="text-2xl mb-1">📝</p>
              <p className="text-sm">No sentence questions yet — add one below.</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs text-slate-400 px-1">{turns.length} sentence question{turns.length !== 1 ? "s" : ""}</p>
              {turns.map((turn) => (
                <SentenceEditor key={turn.id} turn={turn} onSaved={loadDetail} />
              ))}
            </div>
          )}

          {/* Add new sentence question */}
          <AddSentenceForm topicId={selectedTopicId} onSaved={loadDetail} />
        </div>
      )}
    </div>)}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function containsArabic(text) {
  return /[\u0600-\u06FF]/.test(text || "");
}

function getBestDefinition(senses) {
  for (const s of (senses || [])) {
    if (s.definition && s.definition.trim() && !containsArabic(s.definition)) {
      return { id: s.id, text: s.definition };
    }
  }
  return null;
}

// ── WordDefEditor ─────────────────────────────────────────────────────────

function WordDefEditor({ word, isMissing, onSaved }) {
  const best = getBestDefinition(word.senses);
  // First sense of any kind (own or via lemma fallback) — used when no English
  // sense exists so we PATCH the existing record rather than creating a duplicate.
  // For words with multiple non-English senses (e.g. Arabic gloss + empty sense),
  // we update the first one, which is the most-specific sense available.
  const firstAnySense = (word.senses && word.senses.length > 0) ? word.senses[0] : null;

  const [editing, setEditing] = useState(false);
  const [defText, setDefText] = useState(best?.text || "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSave = async () => {
    if (!defText.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (best) {
        // Update the existing English sense
        await patchSense(best.id, { definition: defText.trim() });
      } else if (firstAnySense) {
        // Word has a sense but it's empty or Arabic — update that sense's definition
        // rather than creating a duplicate new sense record
        await patchSense(firstAnySense.id, { definition: defText.trim() });
      } else {
        // Word has no senses at all → create the first one
        await createSense(word.id, defText.trim());
      }
      setEditing(false);
      onSaved();
    } catch (e) {
      setSaveError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-lg border p-3 ${isMissing ? "border-amber-200 bg-amber-50" : "border-slate-100 bg-white"}`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base font-medium text-slate-900" dir="rtl">{word.text}</span>
            {word.transliteration && <span className="text-xs text-slate-400">({word.transliteration})</span>}
            {isMissing && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">Missing</span>
            )}
          </div>
          {!editing && (
            <p className={`text-sm mt-1 ${best ? "text-slate-700" : "text-slate-400 italic"}`}>
              {best ? best.text : "No definition — will show as 'Unknown meaning' in quiz"}
            </p>
          )}
          {editing && (
            <div className="mt-2 space-y-2">
              <input
                type="text"
                value={defText}
                onChange={(e) => setDefText(e.target.value)}
                placeholder="Enter English definition…"
                autoFocus
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
                onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
              />
              {saveError && <p className="text-xs text-red-600">{saveError}</p>}
              <div className="flex gap-2">
                <button onClick={handleSave} disabled={saving || !defText.trim()}
                  className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50">
                  {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={() => { setEditing(false); setDefText(best?.text || ""); setSaveError(null); }}
                  className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
        {!editing && (
          <button onClick={() => { setEditing(true); setDefText(best?.text || ""); }}
            className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors" title="Edit definition">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ── SentenceEditor ────────────────────────────────────────────────────────

function SentenceEditor({ turn, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [arabic, setArabic] = useState(turn.arabic || "");
  const [transliteration, setTransliteration] = useState(turn.transliteration || "");
  const [translation, setTranslation] = useState(turn.translation || "");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleSave = async () => {
    if (!arabic.trim()) { setSaveError("Arabic sentence is required."); return; }
    setSaving(true);
    setSaveError(null);
    try {
      await patchScenarioTurn(turn.id, {
        arabic: arabic.trim(),
        transliteration: transliteration.trim(),
        translation: translation.trim(),
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      setSaveError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this dialogue line? This cannot be undone.")) return;
    setDeleting(true);
    setSaveError(null);
    try {
      await deleteScenarioTurn(turn.id);
      onSaved();
    } catch (e) {
      setSaveError(e.message || "Delete failed");
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setArabic(turn.arabic || "");
    setTransliteration(turn.transliteration || "");
    setTranslation(turn.translation || "");
    setSaveError(null);
  };

  return (
    <div className="rounded-lg border border-slate-100 bg-white p-3">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-slate-400 shrink-0">#{turn.order}</span>
            {turn.speaker && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{turn.speaker}</span>
            )}
          </div>
          {!editing ? (
            <div className="space-y-1">
              <p className="text-base text-slate-900 font-medium" dir="rtl">{turn.arabic}</p>
              {turn.transliteration && <p className="text-xs text-slate-500 italic">{turn.transliteration}</p>}
              {turn.translation && <p className="text-sm text-slate-600">"{turn.translation}"</p>}
            </div>
          ) : (
            <div className="space-y-2 mt-1">
              <div>
                <label className="text-xs text-slate-500 mb-0.5 block">Arabic *</label>
                <input
                  type="text" value={arabic} onChange={(e) => setArabic(e.target.value)}
                  placeholder="Arabic sentence…" dir="rtl" autoFocus
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-0.5 block">Transliteration</label>
                <input
                  type="text" value={transliteration} onChange={(e) => setTransliteration(e.target.value)}
                  placeholder="e.g. ana ismi Ahmed…"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-0.5 block">Translation (English)</label>
                <input
                  type="text" value={translation} onChange={(e) => setTranslation(e.target.value)}
                  placeholder="English translation…"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
              </div>
              {saveError && <p className="text-xs text-red-600">{saveError}</p>}
              <div className="flex gap-2">
                <button onClick={handleSave} disabled={saving || !arabic.trim()}
                  className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50">
                  {saving ? "Saving…" : "Save"}
                </button>
                <button onClick={handleCancel}
                  className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">
                  Cancel
                </button>
                <button onClick={handleDelete} disabled={deleting || saving}
                  className="ml-auto px-3 py-1.5 text-xs rounded-lg bg-red-100 text-red-700 font-medium hover:bg-red-200 disabled:opacity-50">
                  {deleting ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          )}
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)}
            className="shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors" title="Edit sentence">
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
}

// ── AddWordForm ───────────────────────────────────────────────────────────

function AddWordForm({ topicId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [transliteration, setTransliteration] = useState("");
  const [definition, setDefinition] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    if (!text.trim()) { setError("Arabic word is required."); return; }
    if (!definition.trim()) { setError("English definition is required."); return; }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await createQuizWord(topicId, {
        text: text.trim(),
        transliteration: transliteration.trim(),
        definition: definition.trim(),
      });
      setSuccess(true);
      setText(""); setTransliteration(""); setDefinition("");
      onSaved();
      setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError(e.message || "Failed to add word");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <svg className={`w-4 h-4 transition-transform ${open ? "rotate-45" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add new word meaning question
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-200 pt-2">
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Arabic word *</label>
            <input
              type="text" value={text} onChange={(e) => setText(e.target.value)}
              placeholder="e.g. كِتَابٌ" dir="rtl"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Transliteration</label>
            <input
              type="text" value={transliteration} onChange={(e) => setTransliteration(e.target.value)}
              placeholder="e.g. kitābun"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">English definition *</label>
            <input
              type="text" value={definition} onChange={(e) => setDefinition(e.target.value)}
              placeholder="e.g. a book"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {success && <p className="text-xs text-emerald-600">Word added! ✓</p>}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving || !text.trim() || !definition.trim()}
              className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50">
              {saving ? "Adding…" : "Add word"}
            </button>
            <button onClick={() => { setOpen(false); setText(""); setTransliteration(""); setDefinition(""); setError(null); }}
              className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AddSentenceForm ────────────────────────────────────────────────────────

function AddSentenceForm({ topicId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [arabic, setArabic] = useState("");
  const [transliteration, setTransliteration] = useState("");
  const [translation, setTranslation] = useState("");
  const [speaker, setSpeaker] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    if (!arabic.trim()) { setError("Arabic sentence is required."); return; }
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      await createScenarioTurn(topicId, {
        arabic: arabic.trim(),
        transliteration: transliteration.trim(),
        translation: translation.trim(),
        speaker: speaker.trim(),
      });
      setSuccess(true);
      setArabic(""); setTransliteration(""); setTranslation(""); setSpeaker("");
      onSaved();
      setTimeout(() => setSuccess(false), 2000);
    } catch (e) {
      setError(e.message || "Failed to add sentence");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors"
      >
        <svg className={`w-4 h-4 transition-transform ${open ? "rotate-45" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
        Add new sentence building question
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-slate-200 pt-2">
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Arabic sentence *</label>
            <input
              type="text" value={arabic} onChange={(e) => setArabic(e.target.value)}
              placeholder="e.g. أَنَا اسْمِي أَحْمَد" dir="rtl" autoFocus
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Transliteration</label>
            <input
              type="text" value={transliteration} onChange={(e) => setTransliteration(e.target.value)}
              placeholder="e.g. ana ismi Ahmed"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">English translation</label>
            <input
              type="text" value={translation} onChange={(e) => setTranslation(e.target.value)}
              placeholder="e.g. My name is Ahmed"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-0.5 block">Speaker name <span className="text-slate-400">(optional)</span></label>
            <input
              type="text" value={speaker} onChange={(e) => setSpeaker(e.target.value)}
              placeholder="Leave blank for an unattributed line"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-base focus:outline-none focus:ring-2 focus:ring-violet-400"
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
          </div>
          {error && <p className="text-xs text-red-600">{error}</p>}
          {success && <p className="text-xs text-emerald-600">Sentence added! ✓</p>}
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving || !arabic.trim()}
              className="px-3 py-1.5 text-xs rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50">
              {saving ? "Adding…" : "Add sentence"}
            </button>
            <button onClick={() => { setOpen(false); setArabic(""); setTransliteration(""); setTranslation(""); setSpeaker(""); setError(null); }}
              className="px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-600">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── Quranic Curriculum Quiz Editor ───────────────────────────────────────────

function QuranicCurriculumQuizEditor() {
  const [builtinTopics, setBuiltinTopics] = useState([]);
  const [selectedTopicId, setSelectedTopicId] = useState("");
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterMode, setFilterMode] = useState("all"); // "all" | "overrides"

  useEffect(() => {
    fetchBuiltinQuranicTopics()
      .then((data) => {
        const sorted = [...data].sort((a, b) =>
          (a.surah_number - b.surah_number) || (a.ayah_start - b.ayah_start)
        );
        setBuiltinTopics(sorted);
        if (sorted.length > 0) setSelectedTopicId(String(sorted[0].id));
      })
      .catch(() => setError("Failed to load built-in topics"));
  }, []);

  const loadWords = useCallback(async () => {
    if (!selectedTopicId) return;
    setLoading(true);
    setError(null);
    setWords([]);
    try {
      const data = await fetchBuiltinTopicWordOverrides(selectedTopicId);
      setWords(data);
    } catch {
      setError("Failed to load words — check Quran API connectivity.");
    } finally {
      setLoading(false);
    }
  }, [selectedTopicId]);

  useEffect(() => { loadWords(); }, [loadWords]);

  const handleSaveOverride = async (verse_key, position, arabic, correct_meaning) => {
    await saveBuiltinTopicWordOverride(selectedTopicId, { verse_key, position, arabic, correct_meaning });
    setWords((prev) =>
      prev.map((w) =>
        w.verse_key === verse_key && w.position === position
          ? { ...w, correct_meaning, has_override: true }
          : w
      )
    );
  };

  const handleDeleteOverride = async (verse_key, position) => {
    await deleteBuiltinTopicWordOverride(selectedTopicId, { verse_key, position });
    setWords((prev) =>
      prev.map((w) =>
        w.verse_key === verse_key && w.position === position
          ? { ...w, correct_meaning: null, has_override: false, override_id: null }
          : w
      )
    );
  };

  const overrideCount = words.filter((w) => w.has_override).length;
  const displayedWords = filterMode === "overrides" ? words.filter((w) => w.has_override) : words;

  return (
    <div className="space-y-4">
      {/* Topic selector */}
      <div>
        <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
          Built-in Topic
        </label>
        <select
          value={selectedTopicId}
          onChange={(e) => setSelectedTopicId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-base focus:outline-none focus:ring-2 focus:ring-emerald-400"
        >
          {builtinTopics.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.title || `Surah ${t.surah_number}:${t.ayah_start}–${t.ayah_end}`}
            </option>
          ))}
        </select>
      </div>

      {/* Info banner */}
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
        <strong>Quiz words</strong> are fetched live from the Quran API. Set a <strong>correct meaning</strong> override
        to control what the quiz accepts as the right answer for a specific word.
      </div>

      {/* Filter + stats */}
      {!loading && words.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{words.length} words · {overrideCount} override{overrideCount !== 1 ? "s" : ""}</span>
          <div className="flex gap-1 ml-auto">
            {[
              { id: "all", label: "All" },
              { id: "overrides", label: `Overrides (${overrideCount})` },
            ].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilterMode(f.id)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  filterMode === f.id
                    ? "bg-emerald-100 text-emerald-800"
                    : "text-slate-500 hover:bg-slate-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading && <p className="text-sm text-slate-500 text-center py-4">Loading words from Quran API…</p>}
      {error && <p className="text-sm text-red-600 text-center py-2">{error}</p>}

      {!loading && displayedWords.length === 0 && !error && (
        <p className="text-sm text-slate-400 text-center py-4">No words found.</p>
      )}

      {/* Word list */}
      <div className="space-y-1">
        {displayedWords.map((w) => (
          <QuranWordOverrideRow
            key={`${w.verse_key}-${w.position}`}
            word={w}
            onSave={handleSaveOverride}
            onDelete={handleDeleteOverride}
          />
        ))}
      </div>
    </div>
  );
}

function QuranWordOverrideRow({ word, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(word.correct_meaning || word.original_meaning || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const handleSave = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    setErr(null);
    try {
      await onSave(word.verse_key, word.position, word.arabic, draft.trim());
      setEditing(false);
    } catch {
      setErr("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setErr(null);
    try {
      await onDelete(word.verse_key, word.position);
      setEditing(false);
      setDraft(word.original_meaning || "");
    } catch {
      setErr("Delete failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`rounded-lg border px-3 py-2.5 text-sm ${
      word.has_override ? "border-emerald-200 bg-emerald-50" : "border-slate-100 bg-white"
    }`}>
      <div className="flex items-start gap-3">
        {/* Arabic word */}
        <span className="font-arabic text-lg text-slate-800 min-w-[3rem] text-right" dir="rtl">
          {word.arabic}
        </span>
        {/* Verse ref */}
        <span className="text-xs text-slate-400 mt-1 shrink-0">{word.verse_key}</span>

        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-1.5">
              <input
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
                placeholder="Correct meaning…"
                className="w-full px-2 py-1 text-sm border border-emerald-300 rounded focus:outline-none focus:ring-2 focus:ring-emerald-400"
                autoFocus
              />
              {err && <p className="text-xs text-red-600">{err}</p>}
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={saving || !draft.trim()}
                  className="px-3 py-1 text-xs font-medium rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={() => { setEditing(false); setDraft(word.correct_meaning || word.original_meaning || ""); }}
                  className="px-3 py-1 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                {word.has_override && (
                  <button
                    onClick={handleDelete}
                    disabled={saving}
                    className="px-3 py-1 text-xs font-medium rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 ml-auto"
                  >
                    Remove override
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                {word.has_override ? (
                  <div>
                    <span className="text-slate-800 font-medium">{word.correct_meaning}</span>
                    {word.original_meaning && word.original_meaning !== word.correct_meaning && (
                      <span className="text-xs text-slate-400 ml-2">API: {word.original_meaning}</span>
                    )}
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700">✎ override</span>
                  </div>
                ) : (
                  <span className="text-slate-600">{word.original_meaning}</span>
                )}
              </div>
              <button
                onClick={() => { setDraft(word.correct_meaning || word.original_meaning || ""); setEditing(true); }}
                className="shrink-0 px-2.5 py-1 text-xs font-medium rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                {word.has_override ? "Edit" : "Override"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
